/** Payment buttons that must act once however often they are clicked (a double click, an impatient third click while
 * the browser loads the next page). React Query's `isPending` reaches the button only after a re-render, so a second
 * click in the same moment still found it enabled and sent a second request: two Stripe Checkout sessions from one
 * double click on "Continue to payment" (live charter, 30 Oct). A ref closes that gap.
 *
 * - `useLeaving`: a request that ends by sending the browser elsewhere (Checkout, the billing portal). Busy from the
 *   click until the page is gone, not only while the request runs (the redirect takes a moment); back on the page
 *   through the browser's Back (the page restored from its cache), usable again.
 * - `useOnce`: a request that stays on the page (saving extra users, paying with Lux): one at a time. */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

export function useLeaving<V, D>(opts: { mutationFn: (v: V) => Promise<D>; url: (d: D) => string; onError?: (e: unknown, v: V) => void }) {
  const inFlight = useRef(false);
  const [leaving, setLeaving] = useState(false);
  const m = useMutation<D, unknown, V>({
    mutationFn: opts.mutationFn,
    onSuccess: (d) => {
      setLeaving(true);
      window.location.assign(opts.url(d));
    },
    onError: (e, v) => {
      inFlight.current = false;
      opts.onError?.(e, v);
    },
  });
  useEffect(() => {
    const back = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      inFlight.current = false;
      setLeaving(false);
      m.reset();
    };
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const run = (v: V) => {
    if (inFlight.current) return;
    inFlight.current = true;
    m.mutate(v);
  };
  return { run, busy: m.isPending || leaving, variables: m.variables, error: m.error };
}

export function useOnce<V, D>(opts: { mutationFn: (v: V) => Promise<D>; onSuccess?: (d: D, v: V) => void; onError?: (e: unknown, v: V) => void }) {
  const inFlight = useRef(false);
  const m = useMutation<D, unknown, V>({
    mutationFn: opts.mutationFn,
    onSuccess: opts.onSuccess,
    onError: opts.onError,
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const run = (v: V) => {
    if (inFlight.current) return;
    inFlight.current = true;
    m.mutate(v);
  };
  return { run, busy: m.isPending, variables: m.variables, error: m.error };
}
